import React, { useState, useEffect } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { tokenRequest } from "../../authConfig";
import { callMsGraph } from "../../graph";
import { motion } from "framer-motion";
import { LogOut } from "lucide-react";
import AuthLogger from "../../utils/AuthLogger";

interface UserProfile {
  displayName: string;
  mail: string;
  jobTitle?: string;
  photo?: string;
}

interface UserProfileComponentProps {
  isCollapsed?: boolean;
}

export default function UserProfileComponent({ isCollapsed = false }: UserProfileComponentProps) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      setLoading(true);
      
      // Skip Graph API call if on localhost and having issues
      // Fall back to using ID token claims for basic profile info
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost && process.env.NODE_ENV === 'development') {
        // Use basic profile info from ID token (no Graph API call needed)
        console.log('ℹ️ [UserProfile] Using ID token claims on localhost (skipping Graph API)');
        setUserProfile({
          displayName: account.name || 'User',
          mail: account.username || '',
          jobTitle: undefined,
          photo: undefined
        });
        setLoading(false);
        return;
      }
      
      // Production/dev environment: Try to fetch from Graph API with timeout
      const timeoutId = setTimeout(() => {
        console.warn('⏱️ [UserProfile] Graph API call timed out, using fallback profile data');
        setUserProfile({
          displayName: account.name || 'User',
          mail: account.username || '',
          jobTitle: undefined,
          photo: undefined
        });
        setLoading(false);
      }, 5000); // 5 second timeout
      
      instance.acquireTokenSilent({
        ...tokenRequest,
        account: account
      }).then((response) => {
        clearTimeout(timeoutId);
        if (response) {
          callMsGraph(response.accessToken).then((result) => {
            setUserProfile(result);
            setLoading(false);
          }).catch((error) => {
            console.error("Error fetching user profile:", error);
            // Fallback to basic profile
            setUserProfile({
              displayName: account.name || 'User',
              mail: account.username || '',
              jobTitle: undefined,
              photo: undefined
            });
            setLoading(false);
          });
        }
      }).catch((error) => {
        clearTimeout(timeoutId);
        console.error("Error acquiring token:", error);
        // Fallback to basic profile
        setUserProfile({
          displayName: account.name || 'User',
          mail: account.username || '',
          jobTitle: undefined,
          photo: undefined
        });
        setLoading(false);
      });
    }
  }, [account, instance]);

  const handleLogout = () => {
    // Log the logout event
    const userEmail = userProfile?.mail || account?.username || 'unknown';
    AuthLogger.logLogout(userEmail);
    
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  if (!account) {
    return null;
  }

  // Collapsed view - just show avatar
  if (isCollapsed) {
    return (
      <div className="relative group">
        {loading ? (
          <div className="w-8 h-8 bg-gray-600 rounded-full animate-pulse"></div>
        ) : (
          <>
            {userProfile?.photo ? (
              <img
                src={userProfile.photo}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border-2 border-[#42B0D5] cursor-pointer"
                title={userProfile.displayName || account.name}
              />
            ) : (
              <div 
                className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-medium cursor-pointer"
                title={userProfile?.displayName || account.name}
              >
                {(userProfile?.displayName || account.name)?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            
            {/* Tooltip for logout on hover */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              whileHover={{ opacity: 1, scale: 1 }}
              onClick={handleLogout}
              className="absolute -top-10 left-1/2 transform -translate-x-1/2 p-1.5 bg-dark-accent rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
              title="Sign out"
            >
              <LogOut size={12} />
            </motion.button>
          </>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between w-full"
    >
      {loading ? (
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 bg-gray-600 rounded-full animate-pulse"></div>
          <div className="flex flex-col gap-1">
            <div className="w-20 h-3 bg-gray-600 rounded animate-pulse"></div>
            <div className="w-16 h-2 bg-gray-600 rounded animate-pulse"></div>
          </div>
        </div>
      ) : (
        <>
          {/* User Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* User Avatar */}
            {userProfile?.photo ? (
              <img
                src={userProfile.photo}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border-2 border-[#42B0D5] flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                {(userProfile?.displayName || account.name)?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}

            {/* User Details */}
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-text-primary dark:text-dark-text truncate">
                {userProfile?.displayName || account.name}
              </span>
              {userProfile?.jobTitle && (
                <span className="text-xs text-text-tertiary dark:text-dark-textSecondary truncate">
                  {userProfile.jobTitle}
                </span>
              )}
            </div>
          </div>

          {/* Logout Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="p-1.5 text-text-tertiary dark:text-dark-textSecondary hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
            title="Sign out"
          >
            <LogOut size={14} />
          </motion.button>
        </>
      )}
    </motion.div>
  );
}
